// src/views/student/slice/slice.ts
import { createSlice } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import { StudentGroup, UserData } from '../../../services/ApiService.types';

export const INITIAL_STATE: any = {
  list: [],
  // Grupy uczniów nauczyciela, w kolejności z serwera (dowolnej) — ekrany sortują je same
  groups: [],
  isLoading: false,
  error: null,
  isAdding: false,
  addingError: null,
  isEditing: false,
  editingError: null,
  // Adres, na który poszedł nowy link aktywacyjny przy ostatniej edycji — `null`, gdy nic nie poszło
  activationLinkSentTo: null,
  isDeleting: false,
  deletingError: null,
};

const { name, reducer, ...rest } = createSlice({
  name: 'students',
  initialState: INITIAL_STATE,
  reducers: {
    resetData: (state) => {
      state.list = [];
      state.groups = [];
    },
    startAddingStudent: (state) => {
      state.isAdding = true;
      state.addingError = null;
    },
    setAddingStudentError: (state, action) => {
      state.addingError = action.payload;
    },
    finishAddingStudent: (state) => {
      state.isAdding = false;
    },
    addStudentToList: (state, action) => {
      state.list = [
        action.payload,
        ...state.list,
      ];
    },
    startEditingStudent: (state) => {
      state.isEditing = true;
      state.editingError = null;
      state.activationLinkSentTo = null;
    },
    setEditingStudentError: (state, action) => {
      state.editingError = action.payload;
    },
    setActivationLinkSentTo: (state, action) => {
      state.activationLinkSentTo = action.payload;
    },
    replaceStudent: (state, action) => {
      state.list = state.list.map((student: UserData) => {
        if (student.id === action.payload.id) {
          return action.payload;
        }

        return student;
      });
    },
    finishEditingStudent: (state) => {
      state.isEditing = false;
    },
    startDeletingStudent: (state) => {
      state.isDeleting = true;
      state.deletingError = null;
    },
    finishDeletingStudent: (state) => {
      state.isDeleting = false;
    },
    setDeletingStudentError: (state, action) => {
      state.deletingError = action.payload;
    },
    // Serwer sam wypisuje usuniętego ucznia z jego grup — tu robimy to samo, bez ponownego wczytywania grup
    removeStudent: (state, action) => {
      state.list = state.list.filter((student: UserData) => student.id !== action.payload);
      state.groups = state.groups.map((group: StudentGroup) => ({
        ...group,
        studentIds: group.studentIds.filter((id) => id !== action.payload),
      }));
    },
    setGroups: (state, action: { payload: StudentGroup[] }) => {
      state.groups = action.payload;
    },
    // Nowa grupa trafia na koniec, zmieniona zastępuje poprzednią wersję
    putGroup: (state, action: { payload: StudentGroup }) => {
      const exists = state.groups.some((group: StudentGroup) => group.id === action.payload.id);

      state.groups = exists
        ? state.groups.map((group: StudentGroup) => (group.id === action.payload.id ? action.payload : group))
        : [...state.groups, action.payload];
    },
    removeGroup: (state, action: { payload: number }) => {
      state.groups = state.groups.filter((group: StudentGroup) => group.id !== action.payload);
    },
    startLoadingList: (state) => {
      state.isLoading = true;
    },
    setPageData: (state, action: { payload: { records: any[] } }) => {
      // state.list.push(...action.payload.records);
      state.list = action.payload.records;
    },
    replacePageData: (state, action: { payload: { records: any[] } }) => {
      state.list = action.payload.records;
    },
    finishLoadingList: (state) => {
      state.isLoading = false;
    },
    setLoadingListError: (state, action) => {
      state.error = action.payload;
    },
  },
});

const persistConfig = {
  key: name,
  storage,
  whitelist: [],
};

const persistedReducer = persistReducer(persistConfig, reducer);

export default {
  name,
  reducer: persistedReducer,
  ...rest,
};
